// ----- [ constants.js
const ON_CHANGE_DEBOUNCE_TIMER = 300

const PUB_SUB_EVENTS = {
    cartUpdate: 'cart-update',
    quantityUpdate: 'quantity-update',
    optionValueSelectionChange: 'option-value-selection-change',
    variantChange: 'variant-change',
    cartError: 'cart-error',
}

// ----- [ pubsub.js
let subscribers = {}

function subscribe(eventName, callback) {
    if (subscribers[eventName] === undefined) {
        subscribers[eventName] = []
    }

    subscribers[eventName] = [...subscribers[eventName], callback]

    return function unsubscribe() {
        subscribers[eventName] = subscribers[eventName].filter((cb) => {
            return cb !== callback
        })
    }
}

function publish(eventName, data) {
    if (subscribers[eventName]) {
        const promises = subscribers[eventName].map((callback) => callback(data))
        return Promise.all(promises)
    } else {
        return Promise.resolve()
    }
}

// ----- [ global.js
function getFocusableElements(container) {
    return Array.from(
        container.querySelectorAll(
            "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, textarea:enabled, object, iframe", // #CTL-E
        ),
    )
}

class SectionId {
    static #separator = '__'

    // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
    static parseId(qualifiedSectionId) {
        return qualifiedSectionId.split(SectionId.#separator)[0]
    }

    // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
    static parseSectionName(qualifiedSectionId) {
        return qualifiedSectionId.split(SectionId.#separator)[1]
    }

    // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
    static getIdForSection(sectionId, sectionName) {
        return `${sectionId}${SectionId.#separator}${sectionName}`
    }
}

class HTMLUpdateUtility {
    /**
     * Used to swap an HTML node with a new node.
     * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
     *
     * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
     */
    static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
        preProcessCallbacks?.forEach((callback) => callback(newContent))

        const newNodeWrapper = document.createElement('div')
        HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML)
        const newNode = newNodeWrapper.firstChild

        // dedupe IDs
        const uniqueKey = Date.now()
        oldNode.querySelectorAll('[id], [form]').forEach((element) => {
            element.id && (element.id = `${element.id}-${uniqueKey}`)
            element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`)
        })

        oldNode.parentNode.insertBefore(newNode, oldNode)
        oldNode.style.display = 'none'

        postProcessCallbacks?.forEach((callback) => callback(newNode))

        setTimeout(() => oldNode.remove(), 500)
    }

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    static setInnerHTML(element, html) {
        element.innerHTML = html
        element.querySelectorAll('script').forEach((oldScriptTag) => {
            const newScriptTag = document.createElement('script')
            Array.from(oldScriptTag.attributes).forEach((attribute) => {
                newScriptTag.setAttribute(attribute.name, attribute.value)
            })
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
        })
    }
}

const trapFocusHandlers = {}

function trapFocus(container, elementToFocus = container) {
    var elements = getFocusableElements(container)
    var first = elements[0]
    var last = elements[elements.length - 1]

    removeTrapFocus()

    trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return

        document.addEventListener('keydown', trapFocusHandlers.keydown)
    }

    trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', trapFocusHandlers.keydown)
    }

    trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
            event.preventDefault()
            first.focus()
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
            event.preventDefault()
            last.focus()
        }
    }

    document.addEventListener('focusout', trapFocusHandlers.focusout)
    document.addEventListener('focusin', trapFocusHandlers.focusin)

    elementToFocus.focus()

    if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length)
    }
}

function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
    })
    document.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*')
    })
    document.querySelectorAll('video').forEach((video) => video.pause())
    document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause()
    })
}

function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin)
    document.removeEventListener('focusout', trapFocusHandlers.focusout)
    document.removeEventListener('keydown', trapFocusHandlers.keydown)

    if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return

    const openDetailsElement = event.target.closest('details[open]')
    if (!openDetailsElement) return

    const summaryElement = openDetailsElement.querySelector('summary')
    openDetailsElement.removeAttribute('open')
    summaryElement.setAttribute('aria-expanded', false)
    summaryElement.focus()
}

class QuantityInput extends HTMLElement {
    constructor() {
        super()
        this.input = this.querySelector('input')
        this.changeEvent = new Event('change', { bubbles: true })
        this.input.addEventListener('change', this.onInputChange.bind(this))
        this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)))
    }

    quantityUpdateUnsubscriber = undefined

    connectedCallback() {
        this.validateQtyRules()
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this))
    }

    disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
            this.quantityUpdateUnsubscriber()
        }
    }

    onInputChange(event) {
        this.validateQtyRules()
    }

    // (gh) fixing issue #111
    // Code #CB-E
    onButtonClick(event) {
        event.preventDefault()

        // Use .closest() to find the nearest parent button, accounting for clicks on SVG icons or other children.
        const button = event.target.closest('button')
        const action = button.name // 'plus' or 'minus'
        const previousValue = parseInt(this.input.value)
        const minValue = parseInt(this.input.dataset.min)

        if (action === 'plus') {
            if (previousValue < minValue) {
                this.input.value = minValue
            } else {
                this.input.stepUp()
            }
        } else if (action === 'minus') {
            this.input.stepDown()
            if (parseInt(this.input.value) < minValue) {
                this.input.value = minValue
            }
        }

        if (previousValue !== parseInt(this.input.value)) {
            this.input.dispatchEvent(this.changeEvent)
        }
    }

    validateQtyRules() {
        const value = parseInt(this.input.value)
        if (this.input.min) {
            const buttonMinus = this.querySelector(".quantity__button[name='minus']")
            buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min))
        }
        if (this.input.max) {
            const max = parseInt(this.input.max)
            const buttonPlus = this.querySelector(".quantity__button[name='plus']")
            buttonPlus.classList.toggle('disabled', value >= max)
        }
    }
}
customElements.define('quantity-input', QuantityInput)

function debounce(fn, wait) {
    let t
    return (...args) => {
        clearTimeout(t)
        t = setTimeout(() => fn.apply(this, args), wait)
    }
}

function throttle(fn, delay) {
    let lastCall = 0
    return function (...args) {
        const now = new Date().getTime()
        if (now - lastCall < delay) {
            return
        }
        lastCall = now
        return fn(...args)
    }
}

function fetchConfig(type = 'json') {
    return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    }
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
    window.Shopify = {}
}

Shopify.bind = function (fn, scope) {
    return function () {
        return fn.apply(scope, arguments)
    }
}

Shopify.setSelectorByValue = function (selector, value) {
    for (var i = 0, count = selector.options.length; i < count; i++) {
        var option = selector.options[i]
        if (value == option.value || value == option.innerHTML) {
            selector.selectedIndex = i
            return i
        }
    }
}

Shopify.addListener = function (target, eventName, callback) {
    target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent('on' + eventName, callback)
}

Shopify.postLink = function (path, options) {
    options = options || {}
    var method = options['method'] || 'post'
    var params = options['parameters'] || {}

    var form = document.createElement('form')
    form.setAttribute('method', method)
    form.setAttribute('action', path)

    for (var key in params) {
        var hiddenField = document.createElement('input')
        hiddenField.setAttribute('type', 'hidden')
        hiddenField.setAttribute('name', key)
        hiddenField.setAttribute('value', params[key])
        form.appendChild(hiddenField)
    }
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
}

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
    this.countryEl = document.getElementById(country_domid)
    this.provinceEl = document.getElementById(province_domid)
    this.provinceContainer = document.getElementById(options['hideElement'] || province_domid)

    Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this))

    this.initCountry()
    this.initProvince()
}

Shopify.CountryProvinceSelector.prototype = {
    initCountry: function () {
        var value = this.countryEl.getAttribute('data-default')
        Shopify.setSelectorByValue(this.countryEl, value)
        this.countryHandler()
    },

    initProvince: function () {
        var value = this.provinceEl.getAttribute('data-default')
        if (value && this.provinceEl.options.length > 0) {
            Shopify.setSelectorByValue(this.provinceEl, value)
        }
    },

    countryHandler: function (e) {
        var opt = this.countryEl.options[this.countryEl.selectedIndex]
        var raw = opt.getAttribute('data-provinces')
        var provinces = JSON.parse(raw)

        this.clearOptions(this.provinceEl)
        if (provinces && provinces.length == 0) {
            this.provinceContainer.style.display = 'none'
        } else {
            for (var i = 0; i < provinces.length; i++) {
                var opt = document.createElement('option')
                opt.value = provinces[i][0]
                opt.innerHTML = provinces[i][1]
                this.provinceEl.appendChild(opt)
            }

            this.provinceContainer.style.display = ''
        }
    },

    clearOptions: function (selector) {
        while (selector.firstChild) {
            selector.removeChild(selector.firstChild)
        }
    },

    setOptions: function (selector, values) {
        for (var i = 0, count = values.length; i < values.length; i++) {
            var opt = document.createElement('option')
            opt.value = values[i]
            opt.innerHTML = values[i]
            selector.appendChild(opt)
        }
    },
}

class ModalDialog extends HTMLElement {
    constructor() {
        super()
        this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false))
        this.addEventListener('keyup', (event) => {
            if (event.code.toUpperCase() === 'ESCAPE') this.hide()
        })
        if (this.classList.contains('media-modal')) {
            this.addEventListener('pointerup', (event) => {
                if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide()
            })
        } else {
            this.addEventListener('click', (event) => {
                if (event.target === this) this.hide()
            })
        }
    }

    connectedCallback() {
        if (this.moved) return
        this.moved = true
        document.body.appendChild(this)
    }

    show(opener) {
        this.openedBy = opener
        const popup = this.querySelector('.template-popup')
        document.body.classList.add('overflow-hidden')
        this.setAttribute('open', '')
        if (popup) popup.loadContent()
        trapFocus(this, this.querySelector('[role="dialog"]'))
        window.pauseAllMedia()
    }

    hide() {
        document.body.classList.remove('overflow-hidden')
        document.body.dispatchEvent(new CustomEvent('modalClosed'))
        this.removeAttribute('open')
        removeTrapFocus(this.openedBy)
        window.pauseAllMedia()
    }
}
customElements.define('modal-dialog', ModalDialog)

class ModalOpener extends HTMLElement {
    constructor() {
        super()

        const button = this.querySelector('button')

        if (!button) return
        button.addEventListener('click', () => {
            const modal = document.querySelector(this.getAttribute('data-modal'))
            if (modal) modal.show(button)
        })
    }
}
customElements.define('modal-opener', ModalOpener)

class DeferredMedia extends HTMLElement {
    constructor() {
        super()
        const poster = this.querySelector('[id^="Deferred-Poster-"]')
        if (!poster) return
        poster.addEventListener('click', this.loadContent.bind(this))
    }

    loadContent(focus = true) {
        // === [ CORETEX FYI ] ===
        // Check if there's a <video-player> element
        // If there's a <video-player>, do not run the rest of the logic
        const videoPlayer = this.querySelector('video-player')
        if (!videoPlayer) {
            window.pauseAllMedia()

            const template = this.querySelector('template')
            if (!template) {
                console.error('Template element not found.')
                return
            }

            if (!this.getAttribute('loaded')) {
                const content = document.createElement('div')
                content.appendChild(template.content.firstElementChild.cloneNode(true))

                this.setAttribute('loaded', true)
                const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'))
                if (focus) deferredElement.focus()
                if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute('autoplay')) {
                    // Force autoplay for Safari
                    deferredElement.play()
                }
            }

            // Workaround for safari iframe bug
            const formerStyle = deferredElement.getAttribute('style')
            deferredElement.setAttribute('style', 'display: block;')
            window.setTimeout(() => {
                deferredElement.setAttribute('style', formerStyle)
            }, 0)
        }
    }
}
customElements.define('deferred-media', DeferredMedia)

class SliderComponent extends HTMLElement {
    constructor() {
        super()
        this.slider = this.querySelector('[id^="Slider-"]')
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
        this.enableSliderLooping = false
        this.currentPageElement = this.querySelector('.slider-counter--current')
        this.pageTotalElement = this.querySelector('.slider-counter--total')
        this.prevButton = this.querySelector('button[name="previous"]')
        this.nextButton = this.querySelector('button[name="next"]')

        if (!this.slider || !this.nextButton) return

        this.initPages()
        const resizeObserver = new ResizeObserver((entries) => this.initPages())
        resizeObserver.observe(this.slider)

        this.slider.addEventListener('scroll', this.update.bind(this))
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this))
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this))
    }

    initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0)
        if (this.sliderItemsToShow.length < 2) return
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft
        this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset)
        //this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1; // #CTL-E
        if (this.pageTotalElement) this.totalPages = this.pageTotalElement.getAttribute('counter-total')
        this.update()
    }

    resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]')
        this.initPages()
    }

    update() {
        // Temporarily prevents unneeded updates resulting from variant changes
        // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
        if (!this.slider || !this.nextButton) return

        const previousPage = this.currentPage
        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1

        if (this.currentPageElement && this.pageTotalElement) {
            this.currentPageElement.textContent = this.currentPage
            this.pageTotalElement.textContent = this.totalPages
        }

        if (this.currentPage != previousPage) {
            this.dispatchEvent(
                new CustomEvent('slideChanged', {
                    detail: {
                        currentPage: this.currentPage,
                        currentElement: this.sliderItemsToShow[this.currentPage - 1],
                    },
                }),
            )
        }

        if (this.enableSliderLooping) return

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
            this.prevButton.setAttribute('disabled', 'disabled')
        } else {
            this.prevButton.removeAttribute('disabled')
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
            this.nextButton.setAttribute('disabled', 'disabled')
        } else {
            this.nextButton.removeAttribute('disabled')
        }
    }

    isSlideVisible(element, offset = 0) {
        const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset
        return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft
    }

    onButtonClick(event) {
        event.preventDefault()
        const step = event.currentTarget.dataset.step || 1
        this.slideScrollPosition = event.currentTarget.name === 'next' ? this.slider.scrollLeft + step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset
        this.setSlidePosition(this.slideScrollPosition)
    }

    setSlidePosition(position) {
        this.slider.scrollTo({
            left: position,
        })
    }
}
customElements.define('slider-component', SliderComponent)

class SlideshowComponent extends SliderComponent {
    constructor() {
        super()
        this.sliderControlWrapper = this.querySelector('.slider-buttons')
        this.enableSliderLooping = true

        if (!this.sliderControlWrapper) return

        this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide')
        if (this.sliderItemsToShow.length > 0) this.currentPage = 1

        this.announcementBarSlider = this.querySelector('.announcement-bar-slider')
        // Value below should match --duration-announcement-bar CSS value
        this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0

        this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'))
        this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)))
        this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this))
        this.setSlideVisibility()

        /* #CB-NN 
        if (this.announcementBarSlider) {
        this.announcementBarArrowButtonWasClicked = false;

        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reducedMotion.addEventListener('change', () => {
            if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
        });

        [this.prevButton, this.nextButton].forEach((button) => {
            button.addEventListener(
            'click',
            () => {
                this.announcementBarArrowButtonWasClicked = true;
            },
            { once: true }
            );
        });
        }
    */

        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay()
    }

    setAutoPlay() {
        this.autoplaySpeed = this.slider.dataset.speed * 1000
        this.addEventListener('mouseover', this.focusInHandling.bind(this))
        this.addEventListener('mouseleave', this.focusOutHandling.bind(this))
        this.addEventListener('focusin', this.focusInHandling.bind(this))
        this.addEventListener('focusout', this.focusOutHandling.bind(this))

        if (this.querySelector('.slideshow__autoplay')) {
            this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay')
            this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this))
            this.autoplayButtonIsSetToPlay = true
            this.play()
        } else {
            this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play()
        }
    }

    onButtonClick(event) {
        super.onButtonClick(event)
        this.wasClicked = true

        const isFirstSlide = this.currentPage === 1
        const isLastSlide = this.currentPage === this.sliderItemsToShow.length

        if (!isFirstSlide && !isLastSlide) {
            this.applyAnimationToAnnouncementBar(event.currentTarget.name)
            return
        }

        if (isFirstSlide && event.currentTarget.name === 'previous') {
            this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length
        } else if (isLastSlide && event.currentTarget.name === 'next') {
            this.slideScrollPosition = 0
        }

        this.setSlidePosition(this.slideScrollPosition)

        this.applyAnimationToAnnouncementBar(event.currentTarget.name)
    }

    setSlidePosition(position) {
        if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout)
        this.setPositionTimeout = setTimeout(() => {
            this.slider.scrollTo({
                left: position,
            })
        }, this.announcerBarAnimationDelay)
    }

    update() {
        super.update()
        this.sliderControlButtons = this.querySelectorAll('.slider-counter__link')
        this.prevButton.removeAttribute('disabled')

        if (!this.sliderControlButtons.length) return

        this.sliderControlButtons.forEach((link) => {
            link.classList.remove('slider-counter__link--active')
            link.removeAttribute('aria-current')
        })
        this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active')
        this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true)
    }

    autoPlayToggle() {
        this.togglePlayButtonState(this.autoplayButtonIsSetToPlay)
        this.autoplayButtonIsSetToPlay ? this.pause() : this.play()
        this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay
    }

    focusOutHandling(event) {
        if (this.sliderAutoplayButton) {
            const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target)
            if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return
            this.play()
        } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
            this.play()
        }
    }

    focusInHandling(event) {
        if (this.sliderAutoplayButton) {
            const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target)
            if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
                this.play()
            } else if (this.autoplayButtonIsSetToPlay) {
                this.pause()
            }
        } else if (this.announcementBarSlider.contains(event.target)) {
            this.pause()
        }
    }

    play() {
        this.slider.setAttribute('aria-live', 'off')
        clearInterval(this.autoplay)
        this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed)
    }

    pause() {
        this.slider.setAttribute('aria-live', 'polite')
        clearInterval(this.autoplay)
    }

    togglePlayButtonState(pauseAutoplay) {
        if (pauseAutoplay) {
            this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused')
            this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow)
        } else {
            this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused')
            this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow)
        }
    }

    autoRotateSlides() {
        const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset

        this.setSlidePosition(slideScrollPosition)
        this.applyAnimationToAnnouncementBar()
    }

    setSlideVisibility(event) {
        this.sliderItemsToShow.forEach((item, index) => {
            const linkElements = item.querySelectorAll('a')
            if (index === this.currentPage - 1) {
                if (linkElements.length)
                    linkElements.forEach((button) => {
                        button.removeAttribute('tabindex')
                    })
                item.setAttribute('aria-hidden', 'false')
                item.removeAttribute('tabindex')
            } else {
                if (linkElements.length)
                    linkElements.forEach((button) => {
                        button.setAttribute('tabindex', '-1')
                    })
                item.setAttribute('aria-hidden', 'true')
                item.setAttribute('tabindex', '-1')
            }
        })
        this.wasClicked = false
    }

    /* #CB-NN 
    applyAnimationToAnnouncementBar(button = 'next') {
        if (!this.announcementBarSlider) return;

        const itemsCount = this.sliderItems.length;
        const increment = button === 'next' ? 1 : -1;

        const currentIndex = this.currentPage - 1;
        let nextIndex = (currentIndex + increment) % itemsCount;
        nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

        const nextSlide = this.sliderItems[nextIndex];
        const currentSlide = this.sliderItems[currentIndex];

        const animationClassIn = 'announcement-bar-slider--fade-in';
        const animationClassOut = 'announcement-bar-slider--fade-out';

        const isFirstSlide = currentIndex === 0;
        const isLastSlide = currentIndex === itemsCount - 1;

        const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
        const direction = shouldMoveNext ? 'next' : 'previous';

        currentSlide.classList.add(`${animationClassOut}-${direction}`);
        nextSlide.classList.add(`${animationClassIn}-${direction}`);

        setTimeout(() => {
        currentSlide.classList.remove(`${animationClassOut}-${direction}`);
        nextSlide.classList.remove(`${animationClassIn}-${direction}`);
        }, this.announcerBarAnimationDelay * 2);
    }
    */

    linkToSlide(event) {
        event.preventDefault()
        const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage)
        this.slider.scrollTo({
            left: slideScrollPosition,
        })
    }
}
customElements.define('slideshow-component', SlideshowComponent)

class VariantSelects extends HTMLElement {
    constructor() {
        super()
    }

    connectedCallback() {
        this.addEventListener('change', (event) => {
            const target = this.getInputForEventTarget(event.target)
            this.updateSelectionMetadata(event)

            publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
                data: {
                    event,
                    target,
                    selectedOptionValues: this.selectedOptionValues,
                },
            })
        })
    }

    updateSelectionMetadata({ target }) {
        const { value, tagName } = target

        if (tagName === 'SELECT' && target.selectedOptions.length) {
            Array.from(target.options)
                .find((option) => option.getAttribute('selected'))
                .removeAttribute('selected')
            target.selectedOptions[0].setAttribute('selected', 'selected')

            const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue
            const selectedDropdownSwatchValue = target.closest('.product-form__input').querySelector('[data-selected-value] > .swatch')
            if (!selectedDropdownSwatchValue) return
            if (swatchValue) {
                selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue)
                selectedDropdownSwatchValue.classList.remove('swatch--unavailable')
            } else {
                selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset')
                selectedDropdownSwatchValue.classList.add('swatch--unavailable')
            }

            selectedDropdownSwatchValue.style.setProperty('--swatch-focal-point', target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset')
        } else if (tagName === 'INPUT' && target.type === 'radio') {
            const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]')
            if (selectedSwatchValue) selectedSwatchValue.innerHTML = value
        }
    }

    getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
    }

    get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(({ dataset }) => dataset.optionValueId)
    }
}
customElements.define('variant-selects', VariantSelects)

class ProductRecommendations extends HTMLElement {
    observer = undefined

    constructor() {
        super()
    }

    connectedCallback() {
        this.initializeRecommendations(this.dataset.productId)
    }

    initializeRecommendations(productId) {
        this.observer?.unobserve(this)
        this.observer = new IntersectionObserver(
            (entries, observer) => {
                if (!entries[0].isIntersecting) return
                observer.unobserve(this)
                this.loadRecommendations(productId)
            },
            { rootMargin: '0px 0px 400px 0px' },
        )
        this.observer.observe(this)
    }

    loadRecommendations(productId) {
        fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
            .then((response) => response.text())
            .then((text) => {
                const html = document.createElement('div')
                html.innerHTML = text
                const recommendations = html.querySelector('product-recommendations')

                if (recommendations?.innerHTML.trim().length) {
                    this.innerHTML = recommendations.innerHTML
                }

                // if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) this.remove();  // #CTL-NN

                if (html.querySelector('.grid__item')) {
                    this.classList.add('product-recommendations--loaded')
                }
            })
            .catch((e) => {
                console.error(e)
            })
    }
}
customElements.define('product-recommendations', ProductRecommendations)

// ------- [ product-recents.js]
const RV_KEY = 'rv_handles'
const MAX = 20

// record current product handle (run on product pages only)
const curHandle = window?.Shopify?.routes?.root ? window.location.pathname.split('/').filter(Boolean).pop() : null
if (curHandle) {
    const list = JSON.parse(localStorage.getItem(RV_KEY) || '[]').filter((h) => h !== curHandle)
    list.unshift(curHandle)
    localStorage.setItem(RV_KEY, JSON.stringify(list.slice(0, MAX)))
}

class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
        const grid = this.querySelector('x-grid')
        const limit = Number(this.dataset.limit || 4)
        const cur = location.pathname.split('/').filter(Boolean).pop()
        const handles = JSON.parse(localStorage.getItem(RV_KEY) || '[]')
            .filter((h) => h && h !== cur)
            .slice(0, MAX)

        if (!handles.length) return this.remove()

        let added = 0
        for (const h of handles) {
            if (added >= limit) break
            const html = await fetch(`/products/${encodeURIComponent(h)}?view=card`, { credentials: 'same-origin' }).then((r) => r.text())
            const tmp = document.createElement('div')
            tmp.innerHTML = html.trim()

            const card = tmp.querySelector('#card-product') // ONLY in-stock
            if (!card) continue
            grid.appendChild(card)
            added++
        }
        if (added) {
            this.closest('.related.products')?.classList.remove('no-js-hidden')
        } else {
            this.remove()
        }
    }
}
// ----- customElements.define('recently-viewed', RecentlyViewed)

// ----- [ product-info.js
if (!customElements.get('product-info')) {
    customElements.define(
        'product-info',
        class ProductInfo extends HTMLElement {
            quantityInput = undefined
            quantityForm = undefined
            onVariantChangeUnsubscriber = undefined
            cartUpdateUnsubscriber = undefined
            abortController = undefined
            pendingRequestUrl = null
            preProcessHtmlCallbacks = []
            postProcessHtmlCallbacks = []

            constructor() {
                super()

                this.quantityInput = this.querySelector('.quantity__input')
            }

            connectedCallback() {
                this.initializeProductSwapUtility()

                this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleOptionValueChange.bind(this))

                this.initQuantityHandlers()
                this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }))
            }

            addPreProcessCallback(callback) {
                this.preProcessHtmlCallbacks.push(callback)
            }

            initQuantityHandlers() {
                if (!this.quantityInput) return

                this.quantityForm = this.querySelector('.product-form__quantity')
                if (!this.quantityForm) return

                this.setQuantityBoundries()
                if (!this.dataset.originalSection) {
                    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this))
                }
            }

            disconnectedCallback() {
                this.onVariantChangeUnsubscriber()
                this.cartUpdateUnsubscriber?.()
            }

            initializeProductSwapUtility() {
                this.preProcessHtmlCallbacks.push((html) => html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel')))
                this.postProcessHtmlCallbacks.push((newNode) => {
                    window?.Shopify?.PaymentButton?.init()
                    window?.ProductModel?.loadShopifyXR()
                })
            }

            handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
                if (!this.contains(event.target)) return

                this.resetProductFormState()

                const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url
                this.pendingRequestUrl = productUrl
                const shouldSwapProduct = this.dataset.url !== productUrl
                const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct

                this.renderProductInfo({
                    requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
                    targetId: target.id,
                    callback: shouldSwapProduct ? this.handleSwapProduct(productUrl, shouldFetchFullPage) : this.handleUpdateProductInfo(productUrl),
                })
            }

            resetProductFormState() {
                const productForm = this.productForm
                productForm?.toggleSubmitButton(true)
                //productForm?.handleErrorMessage(); // #CTL-NN
            }

            handleSwapProduct(productUrl, updateFullPage) {
                return (html) => {
                    this.productModal?.remove()

                    const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
                    const variant = this.getSelectedVariant(html.querySelector(selector))
                    this.updateURL(productUrl, variant?.id)

                    if (updateFullPage) {
                        document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML

                        HTMLUpdateUtility.viewTransition(document.querySelector('main'), html.querySelector('main'), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks)
                    } else {
                        HTMLUpdateUtility.viewTransition(this, html.querySelector('product-info'), this.preProcessHtmlCallbacks, this.postProcessHtmlCallbacks)
                    }
                }
            }

            renderProductInfo({ requestUrl, targetId, callback }) {
                this.abortController?.abort()
                this.abortController = new AbortController()

                fetch(requestUrl, { signal: this.abortController.signal })
                    .then((response) => response.text())
                    .then((responseText) => {
                        this.pendingRequestUrl = null
                        const html = new DOMParser().parseFromString(responseText, 'text/html')
                        callback(html)
                    })
                    .then(() => {
                        // set focus to last clicked option value
                        document.querySelector(`#${targetId}`)?.focus()
                    })
                    .catch((error) => {
                        if (error.name === 'AbortError') {
                            console.log('Fetch aborted by user')
                        } else {
                            console.error(error)
                        }
                    })
            }

            getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML
                return !!selectedVariant ? JSON.parse(selectedVariant) : null
            }

            buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
                const params = []

                !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`)

                if (optionValues.length) {
                    params.push(`option_values=${optionValues.join(',')}`)
                }

                return `${url}?${params.join('&')}`
            }

            updateOptionValues(html) {
                const variantSelects = html.querySelector('variant-selects')
                if (variantSelects) {
                    HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
                }
            }

            handleUpdateProductInfo(productUrl) {
                return (html) => {
                    const variant = this.getSelectedVariant(html)
                    if (!variant) {
						let option1_el = document.querySelector('[position="1"]:checked')
						let option_2_default = document.querySelector(`[value="${option1_el.getAttribute('data-default-second')}"]`)?.nextElementSibling

						if (option_2_default) {
							option_2_default.click()
							if (option_2_default.closest('select')) {
								let sel = option_2_default.closest('select')
								sel.value = option1_el.getAttribute('data-default-second')
								sel.dispatchEvent(new Event('change', { bubbles: true }))
							}
							return
						}
						
                    }
                    this.pickupAvailability?.update(variant)
                    this.updateOptionValues(html)
                    this.updateURL(productUrl, variant?.id)
                    this.updateVariantInputs(variant?.id)

                    if (!variant) {
                        this.setUnavailable()
                        return
                    }

                    this.updateMedia(html, variant?.featured_media?.id)

                    const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
                        const source = html.getElementById(`${id}-${this.sectionId}`)
                        const destination = this.querySelector(`#${id}-${this.dataset.section}`)
                        if (source && destination) {
                            destination.innerHTML = source.innerHTML
                            destination.classList.toggle('hidden', shouldHide(source))
                        }
                    }

                    updateSourceFromDestination('price')
                    updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'))
                    updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '')
                    updateSourceFromDestination('Volume')
                    updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'))

                    this.updateQuantityRules(this.sectionId, html)
                    this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden')
                    this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden')

                    this.productForm?.toggleSubmitButton(html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true, window.variantStrings.soldOut)

                    publish(PUB_SUB_EVENTS.variantChange, {
                        data: {
                            sectionId: this.sectionId,
                            html,
                            variant,
                        },
                    })
                }
            }

            updateVariantInputs(variantId) {
                this.querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`).forEach((productForm) => {
                    const input = productForm.querySelector('input[name="id"]')
                    input.value = variantId ?? ''
                    input.dispatchEvent(new Event('change', { bubbles: true }))
                })
            }

            updateURL(url, variantId) {
                this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`)

                if (this.dataset.updateUrl === 'false') return
                window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`)
            }

            setUnavailable() {
                this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable)

                const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules'].map((id) => `#${id}-${this.dataset.section}`).join(', ')
                document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'))
            }

            updateMedia(html, variantFeaturedMediaId) {
                if (!variantFeaturedMediaId) return

                const mediaGallerySource = this.querySelector('media-gallery ul')
                const mediaGalleryDestination = html.querySelector(`media-gallery ul`)

                const refreshSourceData = () => {
                    //if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2); // #CTL-NN
                    const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'))
                    const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId))
                    const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]))
                    return [mediaGallerySourceItems, sourceSet, sourceMap]
                }

                if (mediaGallerySource && mediaGalleryDestination) {
                    let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData()
                    const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'))
                    const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId))
                    let shouldRefresh = false

                    // add items from new data not present in DOM
                    for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
                        if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                            mediaGallerySource.prepend(mediaGalleryDestinationItems[i])
                            shouldRefresh = true
                        }
                    }

                    // remove items from DOM not present in new data
                    for (let i = 0; i < mediaGallerySourceItems.length; i++) {
                        if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                            mediaGallerySourceItems[i].remove()
                            shouldRefresh = true
                        }
                    }

                    // refresh
                    if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData()

                    // if media galleries don't match, sort to match new data order
                    mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
                        const sourceData = sourceMap.get(destinationItem.dataset.mediaId)

                        if (sourceData && sourceData.index !== destinationIndex) {
                            mediaGallerySource.insertBefore(sourceData.item, mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`))

                            // refresh source now that it has been modified
                            ;[mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData()
                        }
                    })
                }

                // set featured media as active in the media gallery
                // #CB-E #C-FE
                // Fix for Chrome page-jump issue when switch between variant w/ image
                window.setTimeout(() => {
                    this.querySelector(`media-gallery`)?.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true)
                }, 400)

                // update media modal
                /* #CB-NN
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        */
            }

            setQuantityBoundries() {
                const data = {
                    cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
                    min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
                    max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
                    step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
                }

                let min = data.min
                const max = data.max === null ? data.max : data.max - data.cartQuantity
                if (max !== null) min = Math.min(min, max)
                if (data.cartQuantity >= data.min) min = Math.min(min, data.step)

                this.quantityInput.min = min

                if (max) {
                    this.quantityInput.max = max
                } else {
                    this.quantityInput.removeAttribute('max')
                }
                this.quantityInput.value = min

                publish(PUB_SUB_EVENTS.quantityUpdate, undefined)
            }

            fetchQuantityRules() {
                const currentVariantId = this.productForm?.variantIdInput?.value
                if (!currentVariantId) return

                this.querySelector('.loading__spinner').classList.remove('hidden') // #CTL-E
                fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
                    .then((response) => response.text())
                    .then((responseText) => {
                        const html = new DOMParser().parseFromString(responseText, 'text/html')
                        this.updateQuantityRules(this.dataset.section, html)
                    })
                    .catch((e) => console.error(e))
                    .finally(() => this.querySelector('.loading__spinner').classList.add('hidden')) // #CTL-E
            }

            updateQuantityRules(sectionId, html) {
                if (!this.quantityInput) return
                this.setQuantityBoundries()

                const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`)
                const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label']
                for (let selector of selectors) {
                    const current = this.quantityForm.querySelector(selector)
                    const updated = quantityFormUpdated.querySelector(selector)
                    if (!current || !updated) continue
                    if (selector === '.quantity__input') {
                        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step']
                        for (let attribute of attributes) {
                            const valueUpdated = updated.getAttribute(attribute)
                            if (valueUpdated !== null) {
                                current.setAttribute(attribute, valueUpdated)
                            } else {
                                current.removeAttribute(attribute)
                            }
                        }
                    } else {
                        current.innerHTML = updated.innerHTML
                    }
                }
            }

            get productForm() {
                return this.querySelector(`product-form`)
            }

            get productModal() {
                return document.querySelector(`#ProductModal-${this.dataset.section}`)
            }

            get pickupAvailability() {
                return this.querySelector(`pickup-availability`)
            }

            get variantSelectors() {
                return this.querySelector('variant-selects')
            }

            get relatedProducts() {
                const relatedProductsSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'related-products')
                return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`)
            }

            get quickOrderList() {
                const quickOrderListSectionId = SectionId.getIdForSection(SectionId.parseId(this.sectionId), 'quick_order_list')
                return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`)
            }

            get sectionId() {
                return this.dataset.originalSection || this.dataset.section
            }
        },
    )
}

// ----- [ product-form.js
if (!customElements.get('product-form')) {
    customElements.define(
        'product-form',
        class ProductForm extends HTMLElement {
            constructor() {
                super()

                this.form = this.querySelector('form')
                this.variantIdInput.disabled = false
                //this.form.addEventListener('submit', this.onSubmitHandler.bind(this)) // #CTL-NN
                //this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer') // #CTL-NN
                this.submitButton = this.querySelector('[type="submit"]')
                this.submitButtonText = this.submitButton?.querySelector('span')

                //if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog') // #CTL-NN
                //this.hideErrors = this.dataset.hideErrors === 'true' // #CTL-NN
            }

            // onSubmitHandler(evt) { } // #CTL-NN
            // handleErrorMessage(errorMessage = false) { }  // #CTL-NN

            // === [ CORETEX FYI ] ===
            // #CB-E
            // Added support for preorder attribute
            toggleSubmitButton(disable = true, text) {
                if (document.querySelector('[is-catalog]')) {
                    // this.submitButtonText.textContent = window.variantStrings.addInquiry
                    // this.submitButton.removeAttribute('disabled')
                } else {
                    if (disable) {
                        this.submitButton.setAttribute('disabled', 'disabled')
                        if (text) this.submitButtonText.textContent = text
                    } else {
                        this.submitButton.removeAttribute('disabled')

                        // Check if the "add to cart" button has preorder="true" attribute
                        const isPreOrder = this.submitButton.getAttribute('preorder') === 'true'

                        if (!isPreOrder) {
                            this.submitButtonText.textContent = window.variantStrings.addToCart
                        } else {
                            this.submitButtonText.textContent = window.variantStrings.preOrder
                        }
                    }
                }
            }

            get variantIdInput() {
                return this.form.querySelector('[name=id]')
            }
        },
    )
}

// ----- [ product-modal.js
if (!customElements.get('product-modal')) {
    customElements.define(
        'product-modal',
        class ProductModal extends ModalDialog {
            constructor() {
                super()
            }

            hide() {
                super.hide()
            }

            show(opener) {
                super.show(opener)
                this.showActiveMedia()
            }

            showActiveMedia() {
                this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`).forEach((element) => {
                    element.classList.remove('active')
                })
                const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`)
                const activeMediaTemplate = activeMedia.querySelector('template')
                const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null
                activeMedia.classList.add('active')
                activeMedia.scrollIntoView()

                const container = this.querySelector('[role="document"]')
                container.scrollLeft = (activeMedia.width - container.clientWidth) / 2

                if (activeMedia.nodeName == 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube')) activeMedia.loadContent()
            }
        },
    )
}

// ----- [ product-model
if (!customElements.get('product-model')) {
    customElements.define(
        'product-model',
        class ProductModel extends DeferredMedia {
            constructor() {
                super()
            }

            loadContent() {
                super.loadContent()

                Shopify.loadFeatures([
                    {
                        name: 'model-viewer-ui',
                        version: '1.0',
                        onLoad: this.setupModelViewerUI.bind(this),
                    },
                ])
            }

            setupModelViewerUI(errors) {
                if (errors) return

                this.modelViewerUI = new Shopify.ModelViewerUI(this.querySelector('model-viewer'))
            }
        },
    )
}

window.ProductModel = {
    loadShopifyXR() {
        Shopify.loadFeatures([
            {
                name: 'shopify-xr',
                version: '1.0',
                onLoad: this.setupShopifyXR.bind(this),
            },
        ])
    },

    setupShopifyXR(errors) {
        if (errors) return

        if (!window.ShopifyXR) {
            document.addEventListener('shopify_xr_initialized', () => this.setupShopifyXR())
            return
        }

        document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
            window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent))
            modelJSON.remove()
        })
        window.ShopifyXR.setupXRElements()
    },
}

window.addEventListener('DOMContentLoaded', () => {
    if (window.ProductModel) window.ProductModel.loadShopifyXR()
})

// ----- [ media-gallery.js
if (!customElements.get('media-gallery')) {
    customElements.define(
        'media-gallery',
        class MediaGallery extends HTMLElement {
            constructor() {
                super()
                this.elements = {
                    liveRegion: this.querySelector('[id^="GalleryStatus"]'),
                    viewer: this.querySelector('[id^="GalleryViewer"]'),
                    thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
                }
                this.mql = window.matchMedia('(min-width: 777px)') // Code #CTL-E
                if (!this.elements.thumbnails) return

                this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500))
                this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                    mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false))
                })
                // if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();  #CTL-NN
            }

            onSlideChanged(event) {
                const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${event.detail.currentElement.dataset.mediaId}"]`)
                this.setActiveThumbnail(thumbnail)
            }

            setActiveMedia(mediaId, prepend) {
                const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) || this.elements.viewer.querySelector('[data-media-id]')

                if (!activeMedia) return

                this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
                    element.classList.remove('is-active')
                })
                activeMedia?.classList?.add('is-active')

                if (prepend) {
                    activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia)

                    if (this.elements.thumbnails) {
                        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
                        activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail)
                    }

                    if (this.elements.viewer.slider) this.elements.viewer.resetPages()
                }

                // this.preventStickyHeader(); // #CTL-NN

                // Code #CB-E
                window.setTimeout(() => {
                    // (gh) fixing issue #111
                    const activeMediaRect = activeMedia.getBoundingClientRect()
                    const isDesktop = this.mql.matches

                    if (isDesktop) {
                        // desktop
                        if (this.dataset.slider === 'desktop' || this.dataset.slider === 'deskmob') {
                            // slider
                            window.setTimeout(() => {
                                activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft })
                            }, 400)
                        } else {
                            // image stack
                            activeMedia.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
                        }
                    } else {
                        // mobile
                        if (this.dataset.slider === 'mobile' || this.dataset.slider === 'deskmob')
                            window.setTimeout(() => {
                                activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft })
                            }, 400)
                    }

                    // Don't scroll if the image is already in view
                    if (activeMediaRect.top > -0.5) return
                    const top = activeMediaRect.top + window.scrollY
                    window.scrollTo({ top: top, behavior: 'smooth' })
                })

                this.playActiveMedia(activeMedia)

                if (!this.elements.thumbnails) return
                const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`)
                this.setActiveThumbnail(activeThumbnail)
                this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition)
            }

            setActiveThumbnail(thumbnail) {
                if (!this.elements.thumbnails || !thumbnail) return

                this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'))
                thumbnail.querySelector('button').setAttribute('aria-current', true)
                if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return

                this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft })
            }

            announceLiveRegion(activeItem, position) {
                const image = activeItem.querySelector('.product__modal-opener--image img')
                if (!image) return
                image.onload = () => {
                    this.elements.liveRegion.setAttribute('aria-hidden', false)
                    this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position)
                    setTimeout(() => {
                        this.elements.liveRegion.setAttribute('aria-hidden', true)
                    }, 2000)
                }
                image.src = image.src
            }

            playActiveMedia(activeItem) {
                window.pauseAllMedia()
                const deferredMedia = activeItem.querySelector('.deferred-media')
                if (deferredMedia) deferredMedia.loadContent(false)
            }

            preventStickyHeader() {
                this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header')
                if (!this.stickyHeader) return
                this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'))
            }

            removeListSemantic() {
                if (!this.elements.viewer.slider) return
                this.elements.viewer.slider.setAttribute('role', 'presentation')
                this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'))
            }
        },
    )
}

// ----- [ recipient-form.js
if (!customElements.get('recipient-form')) {
    customElements.define(
        'recipient-form',
        class RecipientForm extends HTMLElement {
            constructor() {
                super()
                this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`)
                this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`)
                this.checkboxInput.disabled = false
                this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`)
                this.hiddenControlField.disabled = true
                this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`)
                this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`)
                this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`)
                this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`)
                this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`)
                if (this.offsetProperty) this.offsetProperty.value = new Date().getTimezoneOffset().toString()

                this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper')
                this.errorMessageList = this.errorMessageWrapper?.querySelector('ul')
                this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message')
                this.defaultErrorHeader = this.errorMessage?.innerText
                this.currentProductVariantId = this.dataset.productVariantId
                this.addEventListener('change', this.onChange.bind(this))
                this.onChange()
            }

            cartUpdateUnsubscriber = undefined
            variantChangeUnsubscriber = undefined
            cartErrorUnsubscriber = undefined

            connectedCallback() {
                this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
                    if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
                        this.resetRecipientForm()
                    }
                })

                this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
                    if (event.data.sectionId === this.dataset.sectionId) {
                        this.currentProductVariantId = event.data.variant.id.toString()
                    }
                })

                this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
                    if (event.source === 'product-form' && event.productVariantId.toString() === this.currentProductVariantId) {
                        this.displayErrorMessage(event.message, event.errors)
                    }
                })
            }

            disconnectedCallback() {
                if (this.cartUpdateUnsubscriber) {
                    this.cartUpdateUnsubscriber()
                }

                if (this.variantChangeUnsubscriber) {
                    this.variantChangeUnsubscriber()
                }

                if (this.cartErrorUnsubscriber) {
                    this.cartErrorUnsubscriber()
                }
            }

            onChange() {
                if (this.checkboxInput.checked) {
                    this.enableInputFields()
                    // this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded; // #CTL-NN
                } else {
                    this.clearInputFields()
                    this.disableInputFields()
                    this.clearErrorMessage()
                    // this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed; // #CTL-NN
                }
            }

            inputFields() {
                return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput]
            }

            disableableFields() {
                return [...this.inputFields(), this.offsetProperty]
            }

            clearInputFields() {
                this.inputFields().forEach((field) => (field.value = ''))
            }

            enableInputFields() {
                this.disableableFields().forEach((field) => (field.disabled = false))
            }

            disableInputFields() {
                this.disableableFields().forEach((field) => (field.disabled = true))
            }

            displayErrorMessage(title, body) {
                this.clearErrorMessage()
                this.errorMessageWrapper.hidden = false
                if (typeof body === 'object') {
                    this.errorMessage.innerText = this.defaultErrorHeader
                    return Object.entries(body).forEach(([key, value]) => {
                        const errorMessageId = `RecipientForm-${key}-error-${this.dataset.sectionId}`
                        const fieldSelector = `#Recipient-${key}-${this.dataset.sectionId}`
                        const message = `${value.join(', ')}`
                        const errorMessageElement = this.querySelector(`#${errorMessageId}`)
                        const errorTextElement = errorMessageElement?.querySelector('.error-message')
                        if (!errorTextElement) return

                        if (this.errorMessageList) {
                            this.errorMessageList.appendChild(this.createErrorListItem(fieldSelector, message))
                        }

                        errorTextElement.innerText = `${message}.`
                        errorMessageElement.classList.remove('hidden')

                        const inputElement = this[`${key}Input`]
                        if (!inputElement) return

                        inputElement.setAttribute('aria-invalid', true)
                        inputElement.setAttribute('aria-describedby', errorMessageId)
                    })
                }

                this.errorMessage.innerText = body
            }

            createErrorListItem(target, message) {
                const li = document.createElement('li')
                const a = document.createElement('a')
                a.setAttribute('href', target)
                a.innerText = message
                li.appendChild(a)
                li.className = 'error-message'
                return li
            }

            clearErrorMessage() {
                this.errorMessageWrapper.hidden = true

                if (this.errorMessageList) this.errorMessageList.innerHTML = ''

                this.querySelectorAll('.recipient-fields .form__message').forEach((field) => {
                    field.classList.add('hidden')
                    const textField = field.querySelector('.error-message')
                    if (textField) textField.innerText = ''
                })

                ;[this.emailInput, this.messageInput, this.nameInput, this.sendonInput].forEach((inputElement) => {
                    inputElement.setAttribute('aria-invalid', false)
                    inputElement.removeAttribute('aria-describedby')
                })
            }

            resetRecipientForm() {
                if (this.checkboxInput.checked) {
                    this.checkboxInput.checked = false
                    this.clearInputFields()
                    this.clearErrorMessage()
                }
            }
        },
    )
}

// ----- [ share.js
if (!customElements.get('share-button')) {
    customElements.define(
        'share-button',
        class ShareButton extends HTMLElement {
            constructor() {
                super()

                this.elements = {
                    urlInput: this.querySelector('input'),
                }
                this.urlToShare = this.elements.urlInput ? this.elements.urlInput.value : document.location.href
            }

            updateUrl(url) {
                this.urlToShare = url
                this.elements.urlInput.value = url
            }
        },
    )
}

// ----- [ pickup-availability.js
if (!customElements.get('pickup-availability')) {
    customElements.define(
        'pickup-availability',
        class PickupAvailability extends HTMLElement {
            constructor() {
                super()

                if (!this.hasAttribute('available')) return

                this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true)
                this.onClickRefreshList = this.onClickRefreshList.bind(this)
                this.fetchAvailability(this.dataset.variantId)
            }

            fetchAvailability(variantId) {
                if (!variantId) return

                let rootUrl = this.dataset.rootUrl
                if (!rootUrl.endsWith('/')) {
                    rootUrl = rootUrl + '/'
                }
                const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`

                fetch(variantSectionUrl)
                    .then((response) => response.text())
                    .then((text) => {
                        const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section')
                        this.renderPreview(sectionInnerHTML)
                    })
                    .catch((e) => {
                        const button = this.querySelector('button')
                        if (button) button.removeEventListener('click', this.onClickRefreshList)
                        this.renderError()
                    })
            }

            onClickRefreshList() {
                this.fetchAvailability(this.dataset.variantId)
            }

            update(variant) {
                if (variant?.available) {
                    this.fetchAvailability(variant.id)
                } else {
                    this.removeAttribute('available')
                    this.innerHTML = ''
                }
            }

            renderError() {
                this.innerHTML = ''
                this.appendChild(this.errorHtml)

                this.querySelector('button').addEventListener('click', this.onClickRefreshList)
            }

            renderPreview(sectionInnerHTML) {
                const drawer = document.querySelector('pickup-availability-drawer')
                if (drawer) drawer.remove()
                if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
                    this.innerHTML = ''
                    this.removeAttribute('available')
                    return
                }

                this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML
                this.setAttribute('available', '')

                document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'))
                /* #CB-NN-CE
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });
        */

                const button = this.querySelector('button')
                if (button)
                    button.addEventListener('click', (evt) => {
                        document.querySelector('pickup-availability-drawer').show(evt.target)
                    })
            }
        },
    )
}

if (!customElements.get('pickup-availability-drawer')) {
    customElements.define(
        'pickup-availability-drawer',
        class PickupAvailabilityDrawer extends HTMLElement {
            constructor() {
                super()

                this.onBodyClick = this.handleBodyClick.bind(this)

                this.querySelector('button').addEventListener('click', () => {
                    this.hide()
                })

                this.addEventListener('keyup', (event) => {
                    if (event.code.toUpperCase() === 'ESCAPE') this.hide()
                })
            }

            handleBodyClick(evt) {
                const target = evt.target
                if (target != this && !target.closest('pickup-availability-drawer') && target.id != 'ShowPickupAvailabilityDrawer') {
                    this.hide()
                }
            }

            hide() {
                this.removeAttribute('open')
                document.body.removeEventListener('click', this.onBodyClick)
                document.body.classList.remove('overflow-hidden')
                removeTrapFocus(this.focusElement)
            }

            show(focusElement) {
                this.focusElement = focusElement
                this.setAttribute('open', '')
                document.body.addEventListener('click', this.onBodyClick)
                document.body.classList.add('overflow-hidden')
                trapFocus(this)
            }
        },
    )
}
